import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Launch as LaunchIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Delete as DeleteIcon,
  Error as ErrorIcon
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { DebugArtifactsSection, PayloadSnapshotSection } from '../shared/SubmissionDebugSections';

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: 560,
    maxWidth: '100vw'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    background: 'linear-gradient(45deg, #516e76 30%, #accacd 90%)',
    color: theme.palette.common.white
  },
  headerTitle: {
    fontWeight: 600,
    marginRight: theme.spacing(2)
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center'
  },
  content: {
    padding: theme.spacing(3),
    height: '100%',
    overflowY: 'auto',
    backgroundColor: theme.palette.background.default
  },
  section: {
    marginBottom: theme.spacing(3)
  },
  sectionHeader: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center'
  },
  sectionHeaderActions: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  sectionTitle: {
    fontWeight: 600
  },
  statusChip: {
    marginLeft: theme.spacing(1)
  },
  textField: {
    marginBottom: theme.spacing(2)
  },
  accordionRoot: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    boxShadow: 'none',
    '&:before': {
      display: 'none'
    }
  },
  accordionSummary: {
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(1)
  },
  accordionDetails: {
    flexDirection: 'column'
  },
  itemSummary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },
  itemSummaryText: {
    flexGrow: 1,
    marginRight: theme.spacing(1)
  },
  photoList: {
    maxHeight: 220,
    overflowY: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1)
  },
  photoListItem: {
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  emptyState: {
    padding: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2)
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2)
  },
  automationInfo: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem'
  },
  statsPaper: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`
  },
  errorButton: {
    marginTop: theme.spacing(1),
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    '&:hover': {
      borderColor: theme.palette.error.dark,
      backgroundColor: theme.palette.error.light + '10'
    }
  },
  errorDialogTitle: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  errorSection: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`
  },
  errorSectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary
  },
  codeBlock: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    backgroundColor: theme.palette.grey[900],
    color: theme.palette.common.white,
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(0.5),
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  artifactLink: {
    display: 'block',
    marginBottom: theme.spacing(0.5)
  }
}))

const arrayToMultilineText = (value) => {
  if (!Array.isArray(value) || value.length === 0) return '';
  return value.join('\n');
}

const multilineTextToArray = (value) => {
  if (!value) return [];
  return value
    .split(/\n|,/)
    .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
    .filter(Boolean);
}

const buildInitialState = (submission) => {
  const source = submission && typeof submission === 'object' ? submission : {};
  const payloadSnapshot = source.payloadSnapshot || {};
  const jobSiteIdCandidate = source.jobSite || payloadSnapshot.jobSiteId || payloadSnapshot.jobSite?.id || '';
  const jobSiteId = jobSiteIdCandidate
    ? (typeof jobSiteIdCandidate === 'string' ? jobSiteIdCandidate : `${jobSiteIdCandidate}`)
    : '';

  return {
    propertyId: source.propertyId || payloadSnapshot.propertyId || '',
    jobSiteName: source.jobSiteName || payloadSnapshot.jobSite?.name || '',
    jobSiteId,
    attendeesText: arrayToMultilineText(source.attendees || payloadSnapshot.attendees || []),
    items: (source.items || payloadSnapshot.items || []).map((item) => ({ ...item })),
    issues: (source.issues || payloadSnapshot.issues || []).map((issue) => ({ ...issue })),
    photos: (source.allPhotos || payloadSnapshot.allPhotos || []).map((photo) => ({ ...photo })),
    unassignedPhotos: (source.unassignedPhotos || payloadSnapshot.unassignedPhotos || []).map((photo) => ({ ...photo })),
    stats: source.stats || payloadSnapshot.stats || null,
    auditName: payloadSnapshot.auditName || ''
  };
}

const statusColor = (status, theme) => {
  switch (status) {
    case 'success':
      return theme.palette.success.main;
    case 'failed':
      return theme.palette.error.main;
    default:
      return theme.palette.warning.main;
  }
}

const CATEGORY_CONFIG = [
  {
    type: 'maintenance',
    label: 'Maintenance Items',
    singularLabel: 'Maintenance Item',
    addLabel: 'Add Maintenance Item',
    emptyLabel: 'No maintenance items recorded yet.'
  },
  {
    type: 'enhancement',
    label: 'Enhancement Ideas',
    singularLabel: 'Enhancement Idea',
    addLabel: 'Add Enhancement Idea',
    emptyLabel: 'No enhancement ideas recorded yet.'
  },
  {
    type: 'customerNote',
    label: 'Customer Notes',
    singularLabel: 'Customer Note',
    addLabel: 'Add Customer Note',
    emptyLabel: 'No customer notes recorded yet.'
  }
]

const CATEGORY_LIMIT = 10

const extractItemText = (item) => {
  if (!item || typeof item !== 'object') return ''
  // Only description is submitted to Aspire
  const description = item.description
  return (description && typeof description === 'string') ? description.trim() : ''
}

const extractCategoryStrings = (items = [], type) => {
  return items
    .filter((item) => item && item.type === type)
    .map((item) => extractItemText(item))
    .filter(Boolean)
}

const LqaSubmissionDetailDrawer = ({ open, submission, onClose, onResubmit, resubmitting }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [formState, setFormState] = useState(buildInitialState(submission));
  const [localError, setLocalError] = useState(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  useEffect(() => {
    setFormState(buildInitialState(submission));
    setLocalError(null);
    setErrorModalOpen(false);
  }, [submission]);

  const categorizedItems = useMemo(() => {
    const base = CATEGORY_CONFIG.reduce((acc, config) => {
      acc[config.type] = [];
      return acc;
    }, {});

    (formState.items || []).forEach((item, index) => {
      if (!item) return;
      const typeKey = base[item.type] ? item.type : 'maintenance';
      base[typeKey].push({ item: { ...item, type: typeKey }, index });
    });

    return base;
  }, [formState.items]);

  const handleBasicChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleAddItem = useCallback((type) => {
    const config = CATEGORY_CONFIG.find((cfg) => cfg.type === type);
    const currentCount = (formState.items || []).filter((item) => item?.type === type).length;
    if (currentCount >= CATEGORY_LIMIT) {
      setLocalError(`You can only add up to ${CATEGORY_LIMIT} ${config?.label || 'items'}.`);
      return;
    }

    setLocalError(null);
    const newItem = {
      id: `temp-${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type,
      title: '',
      description: '',
      note: '',
      score: null,
      photos: []
    };

    setFormState((prev) => ({
      ...prev,
      items: [...(prev.items || []), newItem]
    }));
  }, [formState.items]);

  const handleRemoveItem = useCallback((globalIndex) => {
    setLocalError(null);
    setFormState((prev) => {
      const nextItems = (prev.items || []).filter((_, idx) => idx !== globalIndex);
      return { ...prev, items: nextItems };
    });
  }, []);

  const handleItemFieldChange = useCallback((index, field, value) => {
    setFormState((prev) => {
      const nextItems = prev.items.map((item, idx) => (idx === index ? { ...item, [field]: value } : item));
      return { ...prev, items: nextItems };
    });
  }, []);

  const handleIssueFieldChange = useCallback((index, field, value) => {
    setFormState((prev) => {
      const nextIssues = prev.issues.map((issue, idx) => (idx === index ? { ...issue, [field]: value } : issue));
      return { ...prev, issues: nextIssues };
    });
  }, []);

  const handleResubmitClick = useCallback(async () => {
    if (!submission || !submission._id) return;
    setLocalError(null);

    const attendees = multilineTextToArray(formState.attendeesText);
    const maintenanceItems = extractCategoryStrings(formState.items, 'maintenance');
    const enhancementItems = extractCategoryStrings(formState.items, 'enhancement');
    const customerNotes = extractCategoryStrings(formState.items, 'customerNote');

    if (maintenanceItems.length > CATEGORY_LIMIT) {
      setLocalError(`You can only include up to ${CATEGORY_LIMIT} maintenance items.`);
      return;
    }
    if (enhancementItems.length > CATEGORY_LIMIT) {
      setLocalError(`You can only include up to ${CATEGORY_LIMIT} enhancement ideas.`);
      return;
    }
    if (customerNotes.length > CATEGORY_LIMIT) {
      setLocalError(`You can only include up to ${CATEGORY_LIMIT} customer notes.`);
      return;
    }

    const overrides = {
      propertyId: formState.propertyId ? formState.propertyId.trim() : undefined,
      jobSiteName: formState.jobSiteName ? formState.jobSiteName.trim() : undefined,
      jobSiteId: formState.jobSiteId ? formState.jobSiteId.trim() : undefined,
      attendees,
      maintenanceItems,
      enhancementItems,
      customerNotes,
      items: formState.items,
      issues: formState.issues,
      photos: formState.photos,
      allPhotos: formState.photos,
      unassignedPhotos: formState.unassignedPhotos,
      stats: {
        maintenanceCount: maintenanceItems.length,
        enhancementCount: enhancementItems.length,
        customerNoteCount: customerNotes.length,
        issueCount: (formState.issues || []).length,
        photoCount: (formState.photos || []).length
      },
      auditName: formState.auditName ? formState.auditName.trim() : undefined
    };

    try {
      await onResubmit(submission._id, { overrides });
    } catch (error) {
      setLocalError(error?.message || 'Failed to resubmit the LQA.');
    }
  }, [formState, onResubmit, submission]);

  const attempts = useMemo(() => {
    if (!submission) return 0;
    const count = submission.resubmissionCount || 0;
    return count + 1;
  }, [submission]);

  const automationDetails = useMemo(() => {
    if (!submission) return null;
    const automation = submission.automation || {};
    return {
      startedAt: automation.startedAt ? moment(automation.startedAt) : null,
      completedAt: automation.completedAt ? moment(automation.completedAt) : null
    };
  }, [submission]);

  const statusChip = useMemo(() => {
    if (!submission) return null;
    const themeColor = statusColor(submission.status, theme);
    return (
      <Chip
        size="small"
        label={submission.status ? submission.status.toUpperCase() : 'UNKNOWN'}
        style={{ backgroundColor: themeColor, color: '#fff' }}
        className={classes.statusChip}
      />
    );
  }, [submission, classes.statusChip, theme]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
    >
      <div className={classes.header}>
        <div>
          <Typography variant="h6" className={classes.headerTitle}>
            {submission?.jobSiteName || submission?.payloadSnapshot?.jobSite?.name || 'LQA Submission'}
          </Typography>
          {submission?.propertyId && (
            <Typography variant="body2">Property ID: {submission.propertyId}</Typography>
          )}
        </div>
        <div className={classes.headerActions}>
          {statusChip}
          <IconButton onClick={onClose} color="inherit">
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      <div className={classes.content}>
        {localError && (
          <Box mb={2}>
            <Alert severity="error" onClose={() => setLocalError(null)}>{localError}</Alert>
          </Box>
        )}

        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>Submission Details</Typography>
          </div>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="textSecondary">Property ID</Typography>
              <Typography variant="body1">{formState.propertyId || '—'}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="textSecondary">Job Site Name</Typography>
              <Typography variant="body1">{formState.jobSiteName || '—'}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="textSecondary">Job Site ID</Typography>
              <Typography variant="body1">{formState.jobSiteId || '—'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Attendees"
                name="attendeesText"
                value={formState.attendeesText}
                onChange={handleBasicChange}
                variant="outlined"
                fullWidth
                multiline
                rows={2}
                className={classes.textField}
                helperText="Enter one attendee per line"
              />
            </Grid>
          </Grid>
        </div>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            Manage maintenance, enhancement, and customer note entries in the sections below. Each category supports up to {CATEGORY_LIMIT} items.
          </Typography>
        </Box>

        {CATEGORY_CONFIG.map((config) => {
          const itemsForType = categorizedItems[config.type] || [];
          const canAdd = itemsForType.length < CATEGORY_LIMIT;
          return (
            <div className={classes.section} key={config.type}>
              <div className={classes.sectionHeader}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>{config.label}</Typography>
                <div className={classes.sectionHeaderActions}>
                  <Typography variant="caption" color="textSecondary">{itemsForType.length}/{CATEGORY_LIMIT}</Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => handleAddItem(config.type)}
                    disabled={!canAdd}
                  >
                    {config.addLabel}
                  </Button>
                </div>
              </div>
              {itemsForType.length === 0 ? (
                <Typography variant="body2" className={classes.emptyState}>{config.emptyLabel}</Typography>
              ) : (
                itemsForType.map(({ item, index: globalIndex }, idx) => (
                  <Accordion key={item.id || `${config.type}-${globalIndex}`} className={classes.accordionRoot}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                      <div className={classes.itemSummary}>
                        <div className={classes.itemSummaryText}>
                          <Typography variant="subtitle2">{config.singularLabel} #{idx + 1}</Typography>
                          <Typography variant="body2" color="textSecondary">{item.description || 'No description provided'}</Typography>
                        </div>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            handleRemoveItem(globalIndex);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                      <TextField
                        label="Description"
                        value={item.description || ''}
                        onChange={(event) => handleItemFieldChange(globalIndex, 'description', event.target.value)}
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={3}
                        className={classes.textField}
                      />
                      {Array.isArray(item.photos) && item.photos.length > 0 && (
                        <Paper elevation={0} className={classes.photoList}>
                          <List dense>
                            {item.photos.map((photo, photoIndex) => (
                              <ListItem key={`${photo.s3Url || photo.fileName}-${photoIndex}`} className={classes.photoListItem}>
                                <ListItemText
                                  primary={photo.fileName || 'Photo'}
                                  secondary={photo.s3Url || 'Unavailable'}
                                />
                                {photo.s3Url && (
                                  <IconButton edge="end" component={Link} href={photo.s3Url} target="_blank" rel="noopener" aria-label="open-photo">
                                    <LaunchIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
              {!canAdd && (
                <Typography variant="caption" color="error">
                  Maximum of {CATEGORY_LIMIT} entries reached.
                </Typography>
              )}
            </div>
          );
        })}

        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>Issues</Typography>
          </div>
          {formState.issues.length === 0 && (
            <Typography variant="body2" color="textSecondary">No issues recorded.</Typography>
          )}
          {formState.issues.map((issue, index) => (
            <Accordion key={issue.id || index} className={classes.accordionRoot}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                <Box display="flex" flexDirection="column" width="100%">
                  <Typography variant="subtitle2">Issue #{index + 1}</Typography>
                  <Typography variant="body2" color="textSecondary">{issue.title || issue.description || 'Untitled'}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <TextField
                  label="Title"
                  value={issue.title || ''}
                  onChange={(event) => handleIssueFieldChange(index, 'title', event.target.value)}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                />
                <TextField
                  label="Description"
                  value={issue.description || ''}
                  onChange={(event) => handleIssueFieldChange(index, 'description', event.target.value)}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  className={classes.textField}
                />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Severity"
                      value={issue.severity || ''}
                      onChange={(event) => handleIssueFieldChange(index, 'severity', event.target.value)}
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Status"
                      value={issue.status || ''}
                      onChange={(event) => handleIssueFieldChange(index, 'status', event.target.value)}
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                    />
                  </Grid>
                </Grid>
                {Array.isArray(issue.photos) && issue.photos.length > 0 && (
                  <Paper elevation={0} className={classes.photoList}>
                    <List dense>
                      {issue.photos.map((photo, photoIndex) => (
                        <ListItem key={`${photo.s3Url || photo.fileName}-${photoIndex}`} className={classes.photoListItem}>
                          <ListItemText
                            primary={photo.fileName || 'Photo'}
                            secondary={photo.s3Url || 'Unavailable'}
                          />
                          {photo.s3Url && (
                            <IconButton edge="end" component={Link} href={photo.s3Url} target="_blank" rel="noopener" aria-label="open-photo">
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </div>

        {formState.photos.length > 0 && (
          <div className={classes.section}>
            <div className={classes.sectionHeader}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>All Photos ({formState.photos.length})</Typography>
            </div>
            <Paper elevation={0} className={classes.photoList}>
              <List dense>
                {formState.photos.map((photo, index) => (
                  <ListItem key={`${photo.s3Url || photo.fileName}-${index}`} className={classes.photoListItem}>
                    <ListItemText
                      primary={photo.fileName || `Photo ${index + 1}`}
                      secondary={photo.s3Url || 'Unavailable'}
                    />
                    {photo.s3Url && (
                      <IconButton edge="end" component={Link} href={photo.s3Url} target="_blank" rel="noopener" aria-label="open-photo">
                        <LaunchIcon fontSize="small" />
                      </IconButton>
                    )}
                  </ListItem>
                ))}
              </List>
            </Paper>
          </div>
        )}

        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>Automation Status</Typography>
          </div>
          <Typography variant="body2" className={classes.automationInfo}>
            Attempts: {attempts}
          </Typography>
          {automationDetails?.startedAt && (
            <Typography variant="body2" className={classes.automationInfo}>
              Last Started: {automationDetails.startedAt.format('MMM D, YYYY h:mm A')}
            </Typography>
          )}
          {automationDetails?.completedAt && (
            <Typography variant="body2" className={classes.automationInfo}>
              Completed: {automationDetails.completedAt.format('MMM D, YYYY h:mm A')}
            </Typography>
          )}
          {submission?.lastResubmittedBy && (
            <Typography variant="body2" className={classes.automationInfo}>
              Last Resubmitted By: {submission.lastResubmittedBy.displayName || submission.lastResubmittedBy.name || submission.lastResubmittedBy.email}
            </Typography>
          )}
          {submission?.status === 'failed' && submission?.failure && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ErrorIcon />}
              onClick={() => setErrorModalOpen(true)}
              className={classes.errorButton}
            >
              View Error Details
            </Button>
          )}
        </div>

        {formState.stats && (
          <div className={classes.section}>
            <div className={classes.sectionHeader}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>Stats Snapshot</Typography>
            </div>
            <Paper elevation={0} className={classes.statsPaper}>
              <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(formState.stats, null, 2)}
              </pre>
            </Paper>
          </div>
        )}

        <div className={classes.section}>
          <PayloadSnapshotSection payloadSnapshot={submission?.payloadSnapshot} />
        </div>

        <Divider />

        <div className={classes.actions}>
          <Button onClick={onClose} variant="outlined" color="default">
            Close
          </Button>
          <div className={classes.actionButtons}>
            <Button
              onClick={handleResubmitClick}
              color="primary"
              variant="contained"
              disabled={resubmitting}
            >
              {resubmitting ? 'Resubmitting…' : 'Resubmit to Aspire'}
            </Button>
          </div>
        </div>
      </div>

      {/* Error Details Modal */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className={classes.errorDialogTitle}>
          <ErrorIcon />
          <span>Error Details</span>
        </DialogTitle>
        <DialogContent dividers>
          {submission?.failure ? (
            <>
              {/* Error Code */}
              <div className={classes.errorSection}>
                <Typography variant="subtitle2" className={classes.errorSectionTitle}>
                  Error Code
                </Typography>
                <Typography variant="body2" color="error">
                  {submission.failure.code || 'UNKNOWN'}
                </Typography>
              </div>

              {/* Error Message */}
              {submission.failure.message && (
                <div className={classes.errorSection}>
                  <Typography variant="subtitle2" className={classes.errorSectionTitle}>
                    Message
                  </Typography>
                  <Typography variant="body2">
                    {submission.failure.message}
                  </Typography>
                </div>
              )}

              {/* Error Type & Stage */}
              {(submission.failure.errorType || submission.failure.errorStage) && (
                <div className={classes.errorSection}>
                  <Typography variant="subtitle2" className={classes.errorSectionTitle}>
                    Error Classification
                  </Typography>
                  <Grid container spacing={2}>
                    {submission.failure.errorType && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">Type</Typography>
                        <Typography variant="body2">{submission.failure.errorType}</Typography>
                      </Grid>
                    )}
                    {submission.failure.errorStage && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">Stage</Typography>
                        <Typography variant="body2">{submission.failure.errorStage}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </div>
              )}

              {/* Retryable Flag */}
              {submission.failure.retryable !== undefined && (
                <div className={classes.errorSection}>
                  <Typography variant="subtitle2" className={classes.errorSectionTitle}>
                    Retryable
                  </Typography>
                  <Chip
                    label={submission.failure.retryable ? 'YES' : 'NO'}
                    color={submission.failure.retryable ? 'primary' : 'default'}
                    size="small"
                  />
                  {submission.failure.retryable && (
                    <Typography variant="caption" display="block" style={{ marginTop: 8 }}>
                      This error can be retried. Try resubmitting with corrected data.
                    </Typography>
                  )}
                </div>
              )}

              {/* Artifacts (Screenshots, Traces, etc.) */}
              {submission.failure.artifacts && (
                <div className={classes.errorSection}>
                  <Typography variant="subtitle2" className={classes.errorSectionTitle}>
                    Debug Artifacts
                  </Typography>
                  <DebugArtifactsSection artifacts={submission.failure.artifacts} />
                </div>
              )}

              {/* Meta Information */}
              {submission.failure.meta && Object.keys(submission.failure.meta).length > 0 && (
                <div className={classes.errorSection}>
                  <Typography variant="subtitle2" className={classes.errorSectionTitle}>
                    Additional Metadata
                  </Typography>
                  <div className={classes.codeBlock}>
                    {JSON.stringify(submission.failure.meta, null, 2)}
                  </div>
                </div>
              )}

              {/* Category Mapping Data (for photo upload errors) */}
              {submission.failure.categoryMappingData && (
                <div className={classes.errorSection}>
                  <Typography variant="subtitle2" className={classes.errorSectionTitle}>
                    Category Mapping Data
                  </Typography>
                  <div className={classes.codeBlock}>
                    {JSON.stringify(submission.failure.categoryMappingData, null, 2)}
                  </div>
                </div>
              )}

              {/* Aspire Response (if relevant) */}
              {submission.failure.aspireResponse && (
                <div className={classes.errorSection}>
                  <Typography variant="subtitle2" className={classes.errorSectionTitle}>
                    Aspire Response
                  </Typography>
                  <div className={classes.codeBlock}>
                    {JSON.stringify(submission.failure.aspireResponse, null, 2)}
                  </div>
                </div>
              )}

              {/* Error Stack Trace */}
              {submission.failure.errorStack && (
                <div className={classes.errorSection}>
                  <Typography variant="subtitle2" className={classes.errorSectionTitle}>
                    Stack Trace
                  </Typography>
                  <div className={classes.codeBlock}>
                    {submission.failure.errorStack}
                  </div>
                </div>
              )}
            </>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No error details available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorModalOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

LqaSubmissionDetailDrawer.propTypes = {
  open: PropTypes.bool,
  submission: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onResubmit: PropTypes.func.isRequired,
  resubmitting: PropTypes.bool
};

LqaSubmissionDetailDrawer.defaultProps = {
  open: false,
  submission: null,
  resubmitting: false
};

export default LqaSubmissionDetailDrawer;
